import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { CONSTANTS_DETAILS } from '../../shared';
import {
  CommonPutPayload,
  EnableDisableResponse,
  ManageLookUpTableData,
} from '../../shared/model/manage-lookup';
import { catchError, lastValueFrom, of } from 'rxjs';
import { SnackbarService } from '../snackbar/snackbar.service';
import { TranslateService } from '@ngx-translate/core';
import {
  CurrencyExchange,
  DefaultCurrencyUomMapper,
  UomCurrencyMapperPayload,
} from '../../shared/model/currency-interface';
import { CellValueChangedEvent } from 'ag-grid-enterprise';
import {
  privilegeRoles,
  RolesPrivilegesResponse,
} from '../../shared/model/roles-interface';
import {
  getAllCategoryOwner,
  RecipeReportData,
  RecipeReportPayload,
  RegionwiseAdmins,
} from '../../shared/model';

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  private baseUrl = environment.APP_BASE_URL;
  public lastUpdatedRecord = signal<CellValueChangedEvent | null>(null);

  constructor(
    private _http: HttpClient,
    public _snackBar: SnackbarService,
    public _translateService: TranslateService,
  ) {}

  /**
   * Fetches lookup data from the server.
   *
   * @return {*}
   * @memberof SettingsService
   */
  public regionLookupData() {
    return lastValueFrom(
      this._http.get<ManageLookUpTableData>(
        this.baseUrl + CONSTANTS_DETAILS.BASE_URL_REGION,
      ),
    ).catch(() => {
      this._snackBar.open({
        message: this._translateService.instant('lookupDataFailed'),
        type: 'error',
      });
      return [];
    }); // Fetch region lookup data
  }
  public countryLookupData(regionId?: number) {
    return lastValueFrom(
      this._http.get<ManageLookUpTableData>(
        `${this.baseUrl}${CONSTANTS_DETAILS.BASE_URL_COUNTRY}${regionId ? `?regionId=${regionId}` : ''}`,
      ),
    ).catch(() => {
      this._snackBar.open({
        message: this._translateService.instant('lookupDataFailed'),
        type: 'error',
      });
    }); // Fetch country lookup data
  }
  public categoryLookupData() {
    return lastValueFrom(
      this._http.get<ManageLookUpTableData[]>(
        this.baseUrl + CONSTANTS_DETAILS.BASE_URL_CATEGORY,
      ),
    ).catch(() => {
      this._snackBar.open({
        message: this._translateService.instant('lookupDataFailed'),
        type: 'error',
      });
      return [];
    }); // Fetch category lookup data
  }
  /**
   * Fetches lookup data based on the provided URL.
   *
   * @param {string} url
   * @return {*}
   * @memberof SettingsService
   */
  public fetchLookupData(url: string) {
    return lastValueFrom(
      this._http.get<ManageLookUpTableData[]>(this.baseUrl + url),
    ).catch(() => {
      this._snackBar.open({
        message: this._translateService.instant('lookupDataFailed'),
        type: 'error',
      });
      return [];
    }); // Fetch lookup data
  }

  /**
   * Enables or disables Lookup based on the provided payload.
   *
   * @param {*} payload
   * @return {*}
   * @memberof SettingsService
   */
  public enableDisableLookup(
    payload: CommonPutPayload,
    url: string,
  ): Promise<EnableDisableResponse> {
    return lastValueFrom(
      this._http.put<EnableDisableResponse>(this.baseUrl + url, payload),
    ).catch(() => {
      this._snackBar.open({
        message: this._translateService.instant('lookupDataFailed'),
        type: 'error',
      });
      return { isSuccess: false, message: '' } as EnableDisableResponse;
    }); // Enable or disable Lookup
  }

  /**
   * Retrieves Currency Exchange Rate master data.
   *
   * @return {*}
   * @memberof SettingsService
   */
  public getCurrencyExchangeMaster(): Promise<{
    exchangeRates: CurrencyExchange[];
  }> {
    return lastValueFrom(
      this._http.get<{ exchangeRates: CurrencyExchange[] }>(
        this.baseUrl + CONSTANTS_DETAILS.CURRENCY_EXCHANGE_RATE_BASE_URL,
      ),
    ).catch((err) => {
      this._snackBar.open({
        message:
          this._translateService.instant('currencyExchangeFetchError') +
          err.message,
        type: 'error',
      });
      return { exchangeRates: [] };
    });
  }

  /**
   * Retrieves Roles and Privileges master data.
   *
   * @return {*}
   * @memberof SettingsService
   */
  public getRolesPrivileges(): Promise<{
    appPrevilegeRolesDetails: RolesPrivilegesResponse[];
  }> {
    return lastValueFrom(
      this._http.get<{ appPrevilegeRolesDetails: RolesPrivilegesResponse[] }>(
        this.baseUrl + CONSTANTS_DETAILS.ROLES_PRIVILEGES_FETCH_URL,
      ),
    ).catch((err) => {
      this._snackBar.open({
        message:
          this._translateService.instant('rolesPrivilegesFetchError') +
          err.message,
        type: 'error',
      });
      return { appPrevilegeRolesDetails: [] };
    });
  }

  /**
   * Enables or disables Privileges for the Roles based on the provided payload.
   *
   * @param {privilegeRoles} payload
   * @return {*}
   * @memberof SettingsService
   */
  public updateRolesPrivileges(
    payload: privilegeRoles,
  ): Promise<{ isSuccess: boolean; message: string }> {
    return lastValueFrom(
      this._http.put<{ isSuccess: boolean; message: string }>(
        this.baseUrl + CONSTANTS_DETAILS.ROLES_PRIVILEGES_UPDATE_URL,
        payload,
      ),
    ).catch(() => {
      this._snackBar.open({
        message: this._translateService.instant('rolesPrivilegesUpdateError'),
        type: 'error',
      });
      return { message: '', isSuccess: false };
    });
  }

  /**
   * Fetches the Currency Exchange Rate History.
   *
   * @param payload
   * @return {*}
   * @memberof SettingsService
   */
  public getCurrencyExchangeHistory(payload: {
    fromCurrencyId: number;
    toCurrencyId: number;
  }): Promise<{ exchangeRates: CurrencyExchange[] }> {
    return lastValueFrom(
      this._http.post<{ exchangeRates: CurrencyExchange[] }>(
        this.baseUrl + CONSTANTS_DETAILS.CURRENCY_EXCHANGE_HISTORY_URL,
        payload,
      ),
    ).catch((err) => {
      this._snackBar.open({
        message:
          this._translateService.instant('currencyExchangeHistoryFetchError') +
          err.message,
        type: 'error',
      });
      return { exchangeRates: [] };
    });
  }

  /**
   * Updating Currency Converison Rate Master with cermId.
   *
   * @param payload
   * @return {*}
   * @memberof SettingsService
   */
  public updateCurrencyExchangeRateMaster(payload: {
    cermId: number;
    conversionRate: number;
  }): Promise<{ isSuccess: boolean; message: string }> {
    return lastValueFrom(
      this._http.put<{ isSuccess: boolean; message: string }>(
        this.baseUrl + CONSTANTS_DETAILS.CURRENCY_EXCHANGE_UPDATE_URL,
        payload,
      ),
    ).catch((err) => {
      this._snackBar.open({
        message:
          this._translateService.instant('currencyExchangeUpdateError') +
          err.message,
        type: 'error',
      });
      return { isSuccess: false, message: '' };
    });
  }

  /**
   * Storing or creating a new Currency Exchange Rate Master.
   *
   * @param {CurrencyExchange} payload
   * @return {*}
   * @memberof SettingsService
   */
  public createCurrencyExchange(
    payload: CurrencyExchange,
  ): Promise<{ isSuccess: boolean; message: string }> {
    return lastValueFrom(
      this._http.post<{ isSuccess: boolean; message: string }>(
        this.baseUrl + CONSTANTS_DETAILS.CURRENCY_EXCHANGE_CREATE_URL,
        payload,
      ),
    ).catch((err) => {
      this._snackBar.open({
        message:
          this._translateService.instant('currencyExchangeCreateError') +
          err.message,
        type: 'error',
      });
      return { isSuccess: false, message: '' };
    });
  }

  /**
   * Retrieves the Default Currency UOM Mapper.
   *
   * @return {*}  {(Promise<DefaultCurrencyUomMapper | []>)}
   * @memberof SettingsService
   */
  public getUomCurrencyMapper(): Promise<DefaultCurrencyUomMapper | []> {
    return lastValueFrom(
      this._http.get<DefaultCurrencyUomMapper>(
        this.baseUrl + CONSTANTS_DETAILS.DEFAULT_CURRENCY_UOM_MAPPER_URL,
      ),
    ).catch((err) => {
      this._snackBar.open({
        message:
          this._translateService.instant('defaultCurrencyUomMapperFetchError') +
          err.message,
        type: 'error',
      });
      return [];
    });
  }

  /**
   *  Creates or updates the UOM Currency Mapper.
   *
   * @memberof SettingsService
   */
  public createUomCurrencyMapper(
    payload: UomCurrencyMapperPayload,
    url: string,
    type: string,
  ): Promise<UomCurrencyMapperPayload> {
    const httpCallUom =
      type === 'put'
        ? this._http.put<UomCurrencyMapperPayload>(this.baseUrl + url, payload)
        : this._http.post<UomCurrencyMapperPayload>(
            this.baseUrl + url,
            payload,
          );
    return lastValueFrom(httpCallUom).catch((err) => {
      this._snackBar.open({
        message:
          this._translateService.instant(
            'defaultCurrencyUomMapperCreateError',
          ) + err.message,
        type: 'error',
      });
      return {
        isSuccess: false,
        message: '',
      } as unknown as UomCurrencyMapperPayload;
    });
  }

  /**
   * Creating recipe report and ingredient details
   *
   * @param {RecipeReportPayload} payload
   * @return {*}
   * @memberof SettingsService
   */
  public getRecipeReport(
    payload: RecipeReportPayload,
  ): Promise<{ data: RecipeReportData; isSuccess: boolean; message: string }> {
    return lastValueFrom(
      this._http
        .post<{
          data: RecipeReportData;
          isSuccess: boolean;
          message: string;
        }>(this.baseUrl + CONSTANTS_DETAILS.RECIPE_REPORT, payload)
        .pipe(
          catchError((err) => {
            this._snackBar.open({
              message: err.message,
              type: 'error',
            });
            return of({
              data: [] as RecipeReportData,
              isSuccess: false,
              message: '',
            });
          }),
        ),
    );
  }

  /**
   * Download recipe and ingredient report
   *
   * @param {RecipeReportPayload} payload
   * @return {*}
   * @memberof SettingsService
   */
  public dowloadRecipeReport(payload: RecipeReportPayload) {
    return lastValueFrom(
      this._http
        .post(this.baseUrl + CONSTANTS_DETAILS.DOWLOAD_EXCEL_REPORT, payload, {
          responseType: 'blob',
          observe: 'response',
        })
        .pipe(
          catchError((err) => {
            this._snackBar.open({
              message: this._translateService.instant(err as string),
              type: 'error',
            });
            throw err;
          }),
        ),
    );
  }

  /**
   * Get the regional wise pricing admins
   *
   * @param {number} regionId
   * @return {*}
   * @memberof SettingsService
   */
  public getRegionWisePricingAdmins(regionId?: number) {
    return lastValueFrom(
      this._http.get<RegionwiseAdmins[]>(
        `${this.baseUrl}${CONSTANTS_DETAILS.BASE_URL_GET_REGIONWISE_PRICING_ADMINS}${regionId ? `?regionId=${regionId}` : ''}`,
      ),
    ).catch((err) => {
      this._snackBar.open({
        message: err.message,
        type: 'error',
      });
    }); // Fetch country lookup data
  }

  /**
   * Delete the regional pricing admin from particular region
   *
   * @param {number} priceApproverId
   * @return {*}
   * @memberof SettingsService
   */
  public deleteRegionWisePricingAdmin(priceApproverId?: number) {
    return lastValueFrom(
      this._http.delete<{ message: string }>(
        `${this.baseUrl}${CONSTANTS_DETAILS.DELETE_REGIONWISE_PRICING_ADMIN}${priceApproverId ? `?priceApproverId=${priceApproverId}` : ''}`,
      ),
    ).catch((err) => {
      this._snackBar.open({
        message: err.message,
        type: 'error',
      });
    }); // Fetch country lookup data
  }

  /**
   * Get the all category owners
   *
   * @return {*}
   * @memberof SettingsService
   */
  public getAllCategoryOwners() {
    return lastValueFrom(
      this._http.get<getAllCategoryOwner>(
        `${this.baseUrl}${CONSTANTS_DETAILS.GET_CATEGORY_OWNERS}`,
      ),
    ).catch((err) => {
      this._snackBar.open({
        message: err.message,
        type: 'error',
      });
    }); // Fetch country lookup data
  }

  /**
   * Add the regional pricing admin to particular region
   *
   * @param {RegionwiseAdmins} payload
   * @return {*}
   * @memberof SettingsService
   */
  public addRegionWisePricingAdmin(
    payload: RegionwiseAdmins,
  ): Promise<{ message: string; isSuccess: boolean; identifier: string }> {
    return lastValueFrom(
      this._http.post<{
        message: string;
        isSuccess: boolean;
        identifier: string;
      }>(
        this.baseUrl + CONSTANTS_DETAILS.ADD_REGIONWISE_PRICING_ADMIN,
        payload,
      ),
    ).catch((err) => {
      this._snackBar.open({
        message: err.message,
        type: 'error',
      });
      return { message: '', isSuccess: false, identifier: '' };
    });
  }
}
